import {
  FiBell as Bell,
  FiMessageSquare as Slack,
  FiMonitor as InApp,
  FiMail as Mail,
  FiCloud as Cloud,
  FiCpu as Cpu,
  FiFolder as Folder,
  FiDollarSign as DollarSign,
  FiShield as Shield,
  FiActivity as Activity,
  FiAlertTriangle as AlertTriangle,
  FiTrendingUp as TrendingUp,
  FiClock as Clock,
  FiAlertCircle as AlertCircle,
  FiDatabase as Database,
} from 'react-icons/fi';
import { CategoryConfigItem, PresetConfig } from './types';

export const GENAIOPS_API_BASE = process.env.REACT_APP_GENAIOPS_API_BASE_URL;

export const CATEGORY_CONFIG: Record<string, CategoryConfigItem> = {
  platformops: {
    name: 'CloudOps',
    icon: Cloud,
    color: 'blue',
    description: 'Cloud environment and infrastructure notifications',
  },
  genaiops: {
    name: 'GenAIOps',
    icon: Cpu,
    color: 'purple',
    description: 'AI model and prompt performance notifications',
  },
  project: {
    name: 'Projects',
    icon: Folder,
    color: 'green',
    description: 'Project updates and milestones',
  },
  billing: {
    name: 'Billing & Budget',
    icon: DollarSign,
    color: 'amber',
    description: 'Budget alerts and billing notifications',
  },
  funding: {
    name: 'Funding',
    icon: DollarSign,
    color: 'emerald',
    description: 'Funding applications and credit notifications',
  },
  security: {
    name: 'Security',
    icon: Shield,
    color: 'red',
    description: 'Security alerts and guardrail violations',
  },
  dataops: {
    name: 'DataOps',
    icon: Database,
    color: 'blue',
    description: 'Data pipeline and ETL job notifications',
  },
  general: {
    name: 'General',
    icon: Bell,
    color: 'gray',
    description: 'General system notifications',
  },
};

export const METRIC_TO_CATEGORY = {
  error_rate: 'genaiops',
  latency_p50: 'genaiops',
  latency_p95: 'genaiops',
  latency_p99: 'genaiops',
  latency_avg: 'genaiops',
  tokens_per_hour: 'genaiops',
  request_count: 'genaiops',
  pass_rate: 'genaiops',
  cache_hit_rate: 'genaiops',
  cost_per_hour: 'platformops',
  cost_per_day: 'platformops',
  resource_utilization: 'platformops',
  idle_resource_count: 'platformops',
  safety_critical_count: 'security',
  safety_high_count: 'security',
  agent_success_rate: 'genaiops',
  tool_failure_rate: 'genaiops',
  quality_score: 'genaiops',
  pipeline_success_rate: 'genaiops',
  data_freshness: 'genaiops',
  schema_drift_count: 'genaiops',
  assessment_completion_rate: 'general',
  support_response_time: 'general',
  timesheet_overdue_count: 'general',
};

export const getMetricCategory = (metric: string): string =>
  (METRIC_TO_CATEGORY as Record<string, string>)[metric] || 'genaiops';

export const CATEGORY_PRESETS: Record<string, PresetConfig[]> = {
  genaiops: [
    {
      name: 'error_rate_spike',
      label: 'Error Rate Spike',
      description: 'Alert when error rate exceeds 10% for 5 minutes',
      severity: 'critical',
      metric: 'error_rate',
      condition: '> 0.1',
      recommended: true,
    },
    {
      name: 'latency_degradation',
      label: 'Latency Degradation',
      description: 'Alert when P95 latency shows anomalous increase',
      severity: 'warning',
      metric: 'latency_p95',
      condition: 'anomaly',
      recommended: true,
    },
    {
      name: 'quality_drop',
      label: 'Quality Drop',
      description: 'Alert when evaluation pass rate drops below 80%',
      severity: 'warning',
      metric: 'pass_rate',
      condition: '< 0.8',
      recommended: false,
    },
    {
      name: 'token_overuse',
      label: 'Token Overuse',
      description: 'Alert when token consumption exceeds 100k/hour',
      severity: 'warning',
      metric: 'tokens_per_hour',
      condition: '> 100000',
      recommended: false,
    },
  ],
  platformops: [
    {
      name: 'cost_overrun',
      label: 'Cost Overrun',
      description: 'Alert when hourly cost exceeds $100',
      severity: 'critical',
      metric: 'cost_per_hour',
      condition: '> 100',
      recommended: true,
    },
    {
      name: 'daily_cost_threshold',
      label: 'Daily Cost Threshold',
      description: 'Alert when daily cost exceeds $500',
      severity: 'warning',
      metric: 'cost_per_day',
      condition: '> 500',
      recommended: true,
    },
    {
      name: 'low_utilization',
      label: 'Low Resource Utilization',
      description: 'Alert when resource utilization drops below 20%',
      severity: 'info',
      metric: 'resource_utilization',
      condition: '< 0.2',
      recommended: false,
    },
    {
      name: 'idle_resources',
      label: 'Idle Resources Detected',
      description: 'Alert when idle resources exceed threshold',
      severity: 'warning',
      metric: 'idle_resource_count',
      condition: '> 5',
      recommended: false,
    },
  ],
  security: [
    {
      name: 'safety_critical',
      label: 'Safety Critical Violations',
      description: 'Alert on critical safety guardrail violations',
      severity: 'critical',
      metric: 'safety_critical_count',
      condition: '> 0',
      recommended: true,
    },
    {
      name: 'safety_high',
      label: 'Safety High Violations',
      description: 'Alert when high severity safety issues occur',
      severity: 'warning',
      metric: 'safety_high_count',
      condition: '> 5',
      recommended: true,
    },
  ],
};

export const PRESET_ICONS = {
  error_rate_spike: TrendingUp,
  latency_degradation: Clock,
  cost_overrun: DollarSign,
  quality_drop: AlertTriangle,
  token_overuse: TrendingUp,
  daily_cost_threshold: DollarSign,
  low_utilization: Activity,
  idle_resources: DollarSign,
  agent_failure_rate: AlertTriangle,
  tool_failure: AlertTriangle,
  safety_critical: Shield,
  safety_high: Shield,
  data_quality_drop: AlertTriangle,
  pipeline_failure: AlertTriangle,
  data_staleness: Clock,
  schema_drift: AlertTriangle,
  assessment_incomplete: AlertCircle,
  support_response_slow: Clock,
  timesheet_overdue: Clock,
};

export const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  warning: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  info: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
};

export const SEVERITY_CONFIG = {
  info: {
    label: 'Info',
    bgColor: 'bg-blue-100 dark:bg-blue-900/40',
    textColor: 'text-blue-700 dark:text-blue-300',
    dotColor: 'bg-blue-500',
  },
  warning: {
    label: 'Warning',
    bgColor: 'bg-amber-100 dark:bg-amber-900/40',
    textColor: 'text-amber-700 dark:text-amber-300',
    dotColor: 'bg-amber-500',
  },
  critical: {
    label: 'Critical',
    bgColor: 'bg-red-100 dark:bg-red-900/40',
    textColor: 'text-red-700 dark:text-red-300',
    dotColor: 'bg-red-500',
  },
};

export const CHANNEL_ICONS = {
  in_app: {
    icon: InApp,
    label: 'In-App',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  email: {
    icon: Mail,
    label: 'Email',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  slack: {
    icon: Slack,
    label: 'Slack',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
  },
};

export const EMAIL_FREQUENCIES = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'digest', label: 'Daily Digest' },
  { value: 'none', label: 'None' },
];

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};
